import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useActiveBar } from "../ActiveBarContext";


function formatTime(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${period}`;
}

function describeSchedule(item) {
  if (item.recurrence === "standing") return "Standing offer";
  if (item.recurrence === "daily") {
    return item.startTime && item.endTime
      ? `Daily, ${formatTime(item.startTime)}–${formatTime(item.endTime)}`
      : "Daily";
  }
  if (item.recurrence === "days") {
    const dayList = item.days?.join(", ") || "";
    return item.startTime && item.endTime
      ? `${dayList}, ${formatTime(item.startTime)}–${formatTime(item.endTime)}`
      : dayList;
  }
  if (item.recurrence === "one-time") {
    return item.startTime && item.endTime
      ? `${item.date}, ${formatTime(item.startTime)}–${formatTime(item.endTime)}`
      : item.date;
  }
  return "";
}

export default function OwnerDashboard() {
  const { myBars, activeBar } = useActiveBar();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      if (!activeBar) {
        setEvents([]);
        setLoadingEvents(false);
        return;
      }
      setLoadingEvents(true);
      const q = query(collection(db, "events"), where("barId", "==", activeBar.id));
      const snap = await getDocs(q);
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoadingEvents(false);
    };
    loadEvents();
  }, [activeBar]);

  // First-run guidance: if this owner hasn't claimed a bar yet, send them
  // straight to Claim Bar instead of a blank, all-zeros Overview page.
  if (myBars.length === 0) {
    return <Navigate to="/owner/claim-bar" replace />;
  }

  // FUTURE (once the pilot ends and a real /owner/subscription page exists):
  // if (!PILOT_MODE && !activeBar?.subscriptionTier) {
  //   return <Navigate to="/owner/subscription" replace />;
  // }

  const specials = activeBar?.specials || [];
  const birthdayDeals = activeBar?.birthdayDeals || [];

  const weeklyEvents = events.filter((e) => e.recurrence === "weekly");
  const oneTimeEvents = events
    .filter((e) => e.recurrence === "one-time")
    .sort((a, b) => (a.date > b.date ? 1 : -1));

  return (
    <>
      <div className="metrics-grid">
        <div className="metric-card">
          <h2>Bars Claimed</h2>
          <p>{myBars.length}</p>
        </div>
        <div className="metric-card">
          <h2>Currently Viewing</h2>
          <p>{activeBar ? activeBar.name || "Unnamed Bar" : "No bar selected"}</p>
        </div>
        <div className="metric-card">
          <h2>Linked Charity</h2>
          <p>{activeBar?.charityId ? (activeBar?.gaming?.charityName || "Linked") : "None yet"}</p>
        </div>
      </div>

      <section className="events-section">
        <h2>Recurring Events</h2>
        {weeklyEvents.length === 0 ? (
          <p style={{ color: "#B8B0C8", marginBottom: "20px" }}>No recurring events yet.</p>
        ) : (
          weeklyEvents.map((ev) => (
            <div className="event-card" key={ev.id}>
              <h3>{ev.title}</h3>
              <p>Every {ev.dayOfWeek} at {formatTime(ev.time)}</p>
            </div>
          ))
        )}
      </section>

      <section className="events-section">
        <h2>Upcoming One-Time Events</h2>
        {loadingEvents ? (
          <p style={{ color: "#B8B0C8" }}>Loading...</p>
        ) : oneTimeEvents.length === 0 ? (
          <p style={{ color: "#B8B0C8", marginBottom: "20px" }}>No upcoming one-time events.</p>
        ) : (
          oneTimeEvents.map((ev) => (
            <div className="event-card" key={ev.id}>
              <h3>{ev.title}</h3>
              <p>{ev.date} at {formatTime(ev.time)}</p>
            </div>
          ))
        )}
      </section>

      <section className="events-section">
        <h2>Active Specials</h2>
        {specials.length === 0 ? (
          <p style={{ color: "#B8B0C8", marginBottom: "20px" }}>No specials added yet.</p>
        ) : (
          specials.map((item) => (
            <div className="event-card" key={item.id}>
              <h3>{item.title}</h3>
              <p>{describeSchedule(item)}</p>
            </div>
          ))
        )}
      </section>

      {birthdayDeals.length > 0 && (
        <section className="events-section">
          <h2>Birthday Deals</h2>
          {birthdayDeals.map((item) => (
            <div className="event-card" key={item.id}>
              <h3>{item.title}</h3>
              {item.details && <p>{item.details}</p>}
            </div>
          ))}
        </section>
      )}
    </>
  );
}
